'use client';

import React, {
  useState,
  useContext,
  useRef,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from 'react';
import { scrollElementIntoViewWithHeaderOffset } from './checkoutGuidance';
import { servicabilityValidation } from 'utils/zipCodeHelper';
import { useBloodVision, ModalityOptions } from '../BloodVisionContext';
import { CircularSpinner } from 'components/shared/CircularSpinner';
import { InfoIcon } from 'assets/icons';
import { OffersModalNewContext } from 'components/shared/OffersModal';
import { ImageModal } from 'components/ring-air/ProductSelectionSection/ImageModal';
import styles from './ZipCodeInput.module.css';

const ZipcodeInfoModal = () => {
  return (
    <div className={styles.zipcodeInfoModal}>
      <h2>Blood Vision pricing may vary by state</h2>
      <p>
        Certain states, such as New York or New Jersey, have unique regulations
        and billing requirements for laboratory services. These regulations may
        mandate additional processing fees or impose restrictions on how lab
        services are billed.
        <br />
        <br /> As a result, if you&apos;re completing your Blood Vision in a
        regulated state, you may incur an extra cost—billed separately by our
        lab partner, Bioreference. We recommend checking state-specific
        requirements before scheduling your lab appointment to understand any
        additional charges that may apply.
      </p>
    </div>
  );
};

interface ZipCodeInputProps {
  zipCode: string;
  setZipCode: (zipCode: string) => void;
  isLoading: boolean;
  ref?: React.Ref<HTMLDivElement>;
}

// Define a custom ref handler interface instead of using HTMLDivElement
export interface ZipCodeInputRefHandle {
  focus: () => void;
  scrollIntoView: () => void;
}

const ZipCodeInput = forwardRef<ZipCodeInputRefHandle, ZipCodeInputProps>(
  ({ zipCode, setZipCode, isLoading }, ref) => {
    const BloodVisionClient = useBloodVision();

    const [error, setError] = useState<string>('');
    const [isValid, setIsValid] = useState<boolean>(false);
    const [isValidating, setIsValidating] = useState<boolean>(false);
    const [isSubmitted, setSubmitted] = useState<boolean>(false);

    // Create refs for DOM elements
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Exposing methods via ref
    useImperativeHandle(ref, () => ({
      focus: () => {
        inputRef.current?.focus({ preventScroll: true });
      },
      scrollIntoView: () => {
        if (containerRef.current) {
          scrollElementIntoViewWithHeaderOffset(containerRef.current);
        }

        inputRef.current?.focus({ preventScroll: true });
      },
    }));

    const handleZipSubmit = async () => {
      setIsValidating(true);
      setError('');

      const {
        zip,
        error: validationError,
        valid,
        availableModalities,
      } = await servicabilityValidation(zipCode);
      setIsValid(valid);
      setIsValidating(false);

      if (valid) {
        setZipCode(zip || zipCode);
        setIsValid(true);
        setSubmitted(true);
        BloodVisionClient.setAvailableModalityOptions(
          (availableModalities || []).map(
            (modality) => modality as ModalityOptions,
          ),
        );
        setError('');
        BloodVisionClient.setIsZipValidUS?.(true);
      } else {
        setError(validationError || 'Invalid ZIP code');
        BloodVisionClient.setAvailableModalityOptions([]);
        setIsValid(false);
        setSubmitted(false);
      }
    };

    const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replace(/\D/g, '').slice(0, 5);
      if (isSubmitted) setSubmitted(false);
      setZipCode(value);
      BloodVisionClient.setIsZipValidUS?.(false);
    };

    useEffect(() => {
      if (zipCode.length === 5) {
        setError('');
        handleZipSubmit();
      } else {
        setIsValid(false);
        BloodVisionClient.setAvailableModalityOptions([]);
      }
    }, [zipCode]);

    useEffect(() => {
      inputRef.current?.focus();
    }, []);

    // Handle key press (Enter)
    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && isValid) {
        handleZipSubmit();
      }
    };

    const shouldShowZipLoadingIndicator =
      isValidating || (isLoading && zipCode.length === 5);

    const ModalClient = useContext(OffersModalNewContext);

    const openSizeInfoModal = () => {
      ModalClient.set(
        <ImageModal>
          <ZipcodeInfoModal />
        </ImageModal>,
      );
      ModalClient.show();
    };

    // Info icon click handler
    const onInfoClick = () => {
      openSizeInfoModal();
    };

    return (
      <div
        className={styles.zipCodeContainer}
        ref={containerRef}
        role="group"
        aria-labelledby="zipCode-label"
      >
        <div className={styles.zipCodeHeader}>
          <label
            className={styles.zipCodeLabel}
            id="zipCode-label"
            htmlFor="zipCode"
          >
            Enter zip code to check availability.
          </label>
          <button
            className={styles.infoIconContainer}
            onClick={onInfoClick}
            aria-label="Learn why ZIP code affects Blood Vision pricing"
          >
            <InfoIcon
              style={{
                width: '1.6rem',
                height: 'auto',
              }}
              fill="#000"
            />
          </button>
        </div>

        <div className={styles.inputContainer}>
          <div className={styles.inputWrapper}>
            {shouldShowZipLoadingIndicator ? (
              <div className={styles.validCheckmark}>
                <CircularSpinner
                  color="rgba(0,0,0,0.5)"
                  size={16}
                  strokeWidth={2}
                />
              </div>
            ) : isValid && isSubmitted ? (
              <div className={styles.validCheckmark} aria-hidden>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5 13l4 4L19 7"
                    stroke="#4CAF50"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            ) : null}
            <input
              className={styles.zipInput}
              id="zipCode"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="Enter Zip Code"
              value={zipCode}
              maxLength={5}
              onChange={handleZipChange}
              onKeyDown={handleKeyPress}
              disabled={isLoading && zipCode.length === 5}
              data-has-error={
                !!error || (zipCode.length > 0 && zipCode.length < 5)
              }
              ref={inputRef}
              aria-invalid={!!error}
              aria-describedby={error ? 'zipCode-error' : 'zipCode-help'}
              aria-required="true"
            />
          </div>
        </div>
        {error && !isValid ? (
          <p className={styles.errorText} id="zipCode-error" aria-live="polite">
            {error}
          </p>
        ) : zipCode.length > 0 && zipCode.length < 5 ? (
          <p className={styles.hintText} id="zipCode-error" aria-live="polite">
            Please enter a valid 5-digit ZIP code
          </p>
        ) : (
          <p className={styles.helpText} id="zipCode-help">
            * Blood Vision pricing may vary based on your state and lab test
            location.
          </p>
        )}
      </div>
    );
  },
);

ZipCodeInput.displayName = 'ZipCodeInput';

export default ZipCodeInput;
